import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Link } from "#/components/Link";
import { useUser } from "#/contexts/UserContext";
import { replaceParams, UserPagePath } from "#/lib/pagesHelper";
import { Button } from "@bleu.builders/ui";
import {
  DialogTemplatedMessage,
  TemplateMessage,
} from "#/components/CreateEmailLink";

const MINUTE_IN_SECONDS = 60;
const HOUR_IN_SECONDS = 60 * MINUTE_IN_SECONDS;
const DAY_IN_SECONDS = 24 * HOUR_IN_SECONDS;

const CountdownTimer = ({ initialTime }) => {
  const [timeRemaining, setTimeRemaining] = useState(initialTime);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimeRemaining((prevTime) => {
        if (prevTime === 0) {
          clearInterval(timerInterval);
          return 0;
        } else {
          return prevTime - 1;
        }
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, []);

  const [days, hours, minutes, seconds] = [
    Math.floor(timeRemaining / DAY_IN_SECONDS),
    Math.floor((timeRemaining % DAY_IN_SECONDS) / HOUR_IN_SECONDS),
    Math.floor((timeRemaining % HOUR_IN_SECONDS) / MINUTE_IN_SECONDS),
    timeRemaining % MINUTE_IN_SECONDS,
  ];

  const [hoursString, minutesString, secondsString] = [
    hours,
    minutes,
    seconds,
  ].map((time) => time.toString().padStart(2, "0"));

  return (
    <div>
      {days > 0 && `${days}d `}
      {hours > 0 && `${hoursString}h `}
      {minutes > 0 && `${minutesString}m `}
      {secondsString}s.
    </div>
  );
};

function AlertBanner({ children }) {
  return (
    <div className="flex items-center gap-x-6 bg-primary/90 text-destructive-foreground px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
      <div className="text-sm leading-6">
        <strong className="font-semibold flex gap-1">{children}</strong>
      </div>
      <div className="flex flex-1 justify-end"></div>
    </div>
  );
}

function OverdueBanner({ user }) {
  if (user.subscription?.status !== "past_due") return null;

  const currentPeriodEnd = new Date(user.subscription?.current_period_end);
  const endPlus4Days = new Date(
    currentPeriodEnd.getTime() + 4 * 24 * 60 * 60 * 1000,
  ); // 4 days in milliseconds

  const isEnded = new Date() > endPlus4Days;

  if (!isEnded) return null;

  const params = useParams();

  const billingPath = replaceParams(
    UserPagePath.ORGANIZATION_BILLING_SETTINGS,
    params,
  );

  return (
    <AlertBanner>
      <span>
        A sua assinatura está em atraso. Por favor, realize o pagamento para
        continuar tendo acesso.
      </span>
      <Link to={billingPath} className="underline">
        Pagar agora
      </Link>
      ou
      <DialogTemplatedMessage
        template={TemplateMessage.OVERDUE_PAYMENT}
        firstName={user.first_name}
        email={user.email}
      >
        <Button variant="link" className="p-0 m-0 text-white h-auto underline">
          Fale com a nossa equipe
        </Button>
      </DialogTemplatedMessage>
    </AlertBanner>
  );
}

function TrialBanner({ user }) {
  if (user.subscription?.status !== "trialing") return null;

  const remainingTime = Math.floor(
    (new Date(user.subscription.trial_ends_at).getTime() -
      new Date().getTime()) / // this could be incorrect because it's not considering the timezone, right?
      1000,
  );

  return (
    <AlertBanner>
      <span>O seu período de teste acaba em </span>
      <CountdownTimer initialTime={remainingTime} />
      <DialogTemplatedMessage
        template={TemplateMessage.TRIAL_SUBSCRIBE}
        firstName={user.first_name}
        email={user.email}
      >
        <Button variant="link" className="p-0 m-0 text-white h-auto underline">
          Fale com a nossa equipe
        </Button>
      </DialogTemplatedMessage>
      para continuar usando o ClickFiscal.
    </AlertBanner>
  );
}

export function SubscriptionAlertsBanner() {
  const { user } = useUser();

  if (user.is_admin) return null;

  switch (user.subscription?.status) {
    case "trialing":
      return <TrialBanner user={user} />;
    case "past_due":
      return <OverdueBanner user={user} />;
    default:
      return null;
  }
}
